import {
  BrandingKey,
  BuilderKey,
  ControlKey,
  GuardKey,
  MicrositePageKey,
  PreviewKey,
  PublishKey,
  TabsKey
} from '../../TModules';

type Catalogue<T> = { [K in keyof T]: T[K] extends string ? string : Catalogue<T[K]> };

export const translations = {
  [MicrositePageKey]: {
    OtherSettings: 'Other Settings',
    SettingUpdated: 'Setting updated',
    FailedToUpdateSetting: 'Failed to update setting',
    Loading: 'Loading',
    LoadError: 'Failed to load settings',
    AllowCustomNickname: 'Allow users to set a custom nickname',
    AllowCustomNicknameSubtext:
      'When enabled, microsite users can set a unique nickname on their profile that will be used as their Alternate ID.'
  },
  [TabsKey]: {
    Details: 'Details',
    Branding: 'Branding',
    Microsite: 'Microsite',
    ControlSettings: 'Control settings'
  },
  [BrandingKey]: {
    Cards: {
      BrandIdentity: 'Brand Identity',
      Typography: 'Typography',
      BackgroundColors: 'Background Colors',
      TextColors: 'Text Colors',
      SemanticColors: 'Semantic Colors',
      AccentColors: 'Accent Colors',
      Others: 'Others',
      Buttons: 'Buttons'
    },
    Tooltips: {
      BrandIdentity:
        'Configure the core brand identity used across the experience, including application name, brand colors, logo and global border radius.',
      Typography:
        'Define the font family used across the experience to maintain consistent typography and readability.',
      BackgroundColors:
        'Configure background colors for key areas of the experience, including sections, app header, app background and hero areas.',
      TextColors:
        'Define colors for headings, body text, supporting text and links to maintain consistent text styling and readability.',
      SemanticColors:
        'Define standard colors used to communicate meaning and status, such as success, warning, error, information, neutral and promotional states.',
      AccentColors:
        'Configure additional supporting colors used for highlights, visual elements and UI components across the experience.',
      Others:
        'Configure additional styling colors used for interface elements such as primary and secondary icons.',
      Buttons:
        'Configure the appearance of primary and secondary buttons, including fill, text, border, disabled states, height and border radius.'
    },
    Fields: {
      title: 'Application name',
      primary_color: 'Primary brand color',
      secondary_color: 'Secondary brand color',
      logo: 'Logo image',
      border_radius: 'Global Border Radius',
      font: 'Font Family',
      bg_primary_color: 'Primary',
      bg_secondary_color: 'Secondary',
      bg_tertiary_color: 'Tertiary',
      bg_alternative_color: 'Alternative',
      header_color: 'App Header',
      app_bg_color: 'App Background',
      font_color: 'Primary',
      text_secondary_color: 'Secondary',
      text_tertiary_color: 'Tertiary',
      link_color: 'Link',
      success_color: 'Success',
      warning_color: 'Warning',
      error_color: 'Error',
      information_color: 'Information',
      neutral_color: 'Neutral',
      promotional_color: 'Promotional',
      accent_color: 'Accent 1',
      accent_2_color: 'Accent 2',
      accent_3_color: 'Accent 3',
      accent_4_color: 'Accent 4',
      accent_5_color: 'Accent 5',
      accent_6_color: 'Accent 6',
      icon_primary_color: 'Icon primary',
      icon_secondary_color: 'Icon secondary',
      CTA_button_bg_color: 'Fill',
      CTA_button_text_color: 'Text color',
      CTA_button_disabled_bg_color: 'Disabled fill',
      CTA_button_disabled_text_color: 'Disabled text',
      secondary_button_bg_color: 'Fill',
      secondary_button_border_color: 'Border',
      secondary_button_text_color: 'Text color',
      secondary_button_disabled_bg_color: 'Disabled fill',
      secondary_button_disabled_border_color: 'Disabled border',
      secondary_button_disabled_text_color: 'Disabled text',
      button_height: 'Button height (px)',
      button_border_radius: 'Border radius (px)'
    },
    Help: {
      PrimaryColor: 'The primary color is used in places like progress bar highlights',
      Logo: 'PNG or SVG, 120 × 30px recommended',
      BorderRadius: 'Applied to cards, inputs, and badges globally.'
    },
    Radius: {
      Small: 'Small - 4px',
      Medium: 'Medium - 8px',
      Large: 'Large - 16px',
      Sharp: 'Sharp - 0px'
    },
    Px: 'px',
    Replace: 'Replace',
    TooltipLabel: 'About {{section}}',
    ResetToDefault: 'Reset to default',
    Reset: 'Reset',
    Save: 'Save Changes',
    Sample: 'Sample',
    SampleText: 'The quick brown fox jumps',
    UnsupportedFont: '{{font}} (unsupported)',
    Preview: 'Preview',
    PreviewHeading: 'Heading text',
    PreviewBody: 'Body text sample here',
    PreviewCaption: 'Caption or helper text',
    PreviewLink: 'Link text',
    PrimaryButton: 'Primary button',
    SecondaryButton: 'Secondary button',
    SharedSizing: 'Shared sizing',
    ButtonPrimary: 'Primary',
    ButtonSecondary: 'Secondary',
    ButtonSmall: 'Small',
    ButtonDisabled: 'Disabled',
    Errors: {
      Required: 'This field is required',
      invalid_hex: 'Enter a valid HEX color, e.g. #0284C7',
      logo_required: 'Logo image is required',
      unsupported_font: 'This font is no longer supported. Choose another font.',
      invalid_integer: 'Enter a whole number of 0 or more'
    },
    Toasts: {
      Saved: 'Branding saved',
      SaveFailed: 'Could not save branding. Please try again.',
      FixErrors: 'Please fix the highlighted fields before saving.'
    },
    Load: {
      Failed: 'Could not load branding settings.',
      Retry: 'Retry'
    }
  },
  [BuilderKey]: {
    StartingPoint: {
      Title: 'Create your Microsite',
      Subtitle: 'Select how you would like to initiate your customer experience portal.',
      TemplateTitle: 'Start with a template',
      TemplateDescription:
        'Choose from pre-built layout profiles designed for loyalty and point mechanics.',
      BlankTitle: 'Start with blank canvas',
      BlankDescription:
        'Begin with an empty workspace and drag components onto the builder manually.',
      Continue: 'Continue',
      ReadOnly: 'You have view-only access, so you cannot create the microsite.'
    },
    Existing: {
      Title: 'Your saved microsite',
      Published: 'Published: {{name}}',
      PublishedAt: 'Published {{date}}',
      Draft: 'Draft: {{name}}',
      DraftUpdatedAt: 'Last saved {{date}}',
      Label: '{{template}} · {{theme}}',
      BlankCanvas: 'Blank Canvas',
      BrandingTheme: 'Global Branding Theme',
      TemplateTheme: 'Template Default Theme',
      View: 'View',
      OverwriteDraft: 'Overwrite Draft Template',
      OverwritePublished: 'Overwrite Published Template',
      Duplicate: {
        Title: 'Template already exists',
        Published:
          'This template is already published. To make changes, please click View, edit the existing template, and publish it again.',
        Draft:
          'This template already exists as a draft. To make changes, please click View and edit the existing draft.',
        Close: 'OK'
      },
      Confirm: {
        Title: 'Replace your saved page?',
        Published:
          'You already have a published template ({{current}}). Starting {{next}} will replace your saved draft when you save, and replace the live page when you publish. Continue?',
        Draft:
          'You already have a saved draft ({{current}}). Starting {{next}} will replace your saved draft when you save. Continue?',
        Continue: 'Continue',
        Cancel: 'Cancel'
      }
    },
    Templates: {
      Title: 'Choose a theme template',
      Subtitle: 'Select a pre-integrated template to define your rewards experience.',
      Back: 'Back',
      Empty: 'No templates are available.',
      UseSelected: 'Use selected template',
      Descriptions: {
        point_utilization: 'Instant point spend mechanisms and catalog displays.',
        hybrid_driven: 'Points, challenges, and brand stats in balance.',
        progress_driven: 'Gamified milestones, levels, and point metrics.',
        reward_driven: 'Trending prizes and location-based nearby deals.'
      }
    },
    ThemeSource: {
      Title: 'Select theme source',
      Description: 'Choose where the colors and typography for this template come from.',
      TemplateDefault: 'Use Template Default Theme settings',
      TemplateDefaultHelp:
        "Applies the template's predefined colors and typography to all sections.",
      Branding: 'Use Global Branding settings',
      BrandingHelp: 'Applies your latest saved Branding colors and typography to all sections.',
      BrandingBlocked:
        'Global Branding is incomplete or invalid. Complete and save it on the Branding tab first.',
      GoToBranding: 'Go to Branding',
      Confirm: 'Continue',
      Cancel: 'Cancel'
    },
    Editor: {
      PageStructure: 'Page Structure',
      Sections: 'Sections',
      ReorderHint: 'Drag sections to reorder',
      AddSection: 'Add section',
      AddSectionHint: 'Drag sections into canvas or click to add',
      Settings: '{{name}} Settings',
      Layout: 'Layout',
      LayoutUnavailable: 'No layouts are available for this section yet.',
      SelectSection: 'Select a section to configure it.',
      Card: 'Card {{number}}',
      NearbyReward: {
        RewardDistance: 'Reward distance'
      },
      CategoryReward: {
        RewardsCategoryFilter: 'Rewards category filter',
        RewardsCategoryFilterTooltip:
          'Displays all configured categories to filter rewards by them.'
      },
      PointRedemption: {
        DisplayImage: 'Display image',
        DisplayImageHint: '{{size}}px by {{size}}px recommended',
        TitleText: 'Title text',
        SubtitleText: 'Subtitle text',
        SemanticText: 'Semantic text',
        PointsText: 'Points text'
      },
      TransferPoints: {
        DisplayImage: 'Display image',
        DisplayImageHint: '{{size}}px by {{size}}px recommended',
        BackgroundNumber: 'Background {{number}}',
        TitleText: 'Title text',
        SubtitleText: 'Subtitle text'
      },
      AvailableChallenges: {
        CampaignTitle: 'Campaign Title',
        ChallengeTypeFilter: 'Challenge type filter',
        ChallengeTypeFilterTooltip:
          'Displays all challenge type to filter available challenges by them.'
      },
      GamesAndChallenges: {
        AppearanceTooltips: {
          Accent1: 'New badge background',
          Accent2: 'Upcoming badge background',
          Accent3: 'End date badge background',
          Semantic1: 'New badge text',
          Semantic2: 'Upcoming badge text',
          Semantic3: 'End date badge text'
        },
        CardBackground: 'Card Background',
        ChallengeTitle: 'Challenge title',
        DescriptionText: 'Description text',
        AccentNumber: 'Accent {{number}}',
        SemanticNumber: 'Semantic {{number}}',
        Description: 'Description',
        NewBadge: 'New badge',
        UpcomingBadge: 'Upcoming badge',
        EndDateBadge: 'End date badge',
        CarouselContentTooltip:
          'Choose how many challenges to display in the carousel. Leave blank to display all challenges with infinite scrolling.',
        ChallengesToDisplay: 'Challenges to display',
        SeeAllHint: 'Navigates users to the Challenges listing page to view all challenges.'
      },
      FeaturedReward: {
        AccentTooltip: 'Badge background',
        SemanticTooltip: 'Badge text',
        Visibility: 'Visibility',
        PointsBadge: 'Points badge',
        RewardTitle: 'Reward title',
        MerchantName: 'Merchant name',
        SellingDate: 'Selling date',
        CarouselContent: 'Carousel content',
        CarouselContentTooltip:
          'Choose how many rewards to display in the carousel. Leave blank to display all rewards with infinite scrolling.',
        RewardsToDisplay: 'Rewards to display',
        ShowSeeAll: 'Show "See all" button',
        SeeAllHint: 'Navigates users to the Rewards listing page to view all rewards.'
      },
      SectionSettings: {
        Title: 'Section settings',
        ShowTitle: 'Show title',
        TitleText: 'Title text',
        TranslateTitle: 'Translate title',
        Translation: 'Translation',
        BackToSettings: 'Back to settings',
        TranslationLanguages: {
          en: 'English',
          ar: 'Arabic',
          fr: 'French',
          id: 'Indonesian',
          it: 'Italian',
          vi: 'Vietnamese'
        },
        TextColor: 'Text color',
        SectionBackground: 'Section background',
        BackgroundColor: 'Background color',
        Margin: 'Margin',
        Top: 'Top',
        Bottom: 'Bottom'
      },
      Appearance: {
        Title: 'Appearance',
        ResetToTemplate: 'Reset to template',
        ResetToBranding: 'Reset to branding',
        Background: 'Background',
        CardBackground: 'Card background',
        CardBackground1: 'Card background 1',
        CardBackground2: 'Card background 2',
        RankBackground: 'Rank background',
        Alternate: 'Alternate',
        TextNumber: 'Text {{number}}',
        LeaderboardTextTooltips: {
          Text1: 'Rank',
          Text2: 'Label',
          Text3: 'Leaderboard title'
        },
        ActiveStreakTextTooltips: {
          Text1: 'Streak count',
          Text2: 'Streak Activity',
          Text3: 'Description'
        },
        Text: 'Text',
        TextColor: 'Text',
        ExpiryTextColor: 'Semantic',
        Accent: 'Accent',
        Semantic: 'Semantic',
        Icon: 'Icon',
        ButtonType: 'Button type',
        PrimaryButton: 'Primary Button',
        SecondaryButton: 'Secondary Button'
      }
    },
    Panel: {
      Title: 'Sections',
      Add: 'Add section',
      Empty: 'No sections added yet.',
      MoveUp: 'Move {{name}} up',
      MoveDown: 'Move {{name}} down',
      Remove: 'Remove {{name}}',
      LayoutLabel: '{{name}} layout',
      Layouts: {
        Carousel: 'Carousel',
        List: 'List',
        Grid: 'Grid'
      },
      QuickLinksLabel: 'Quick links',
      QuickLinksPlaceholder: 'Select quick links',
      QuickActionLabel: 'Action {{number}}',
      QuickActionPlaceholder: 'Select a quick link',
      QuickActionUpload: 'Upload',
      QuickActionUploaded: 'Uploaded',
      QuickActionUploadLabel: 'Upload image for action {{number}}',
      QuickActionImageHint: '48px by 48px recommended',
      QuickActionImageTooLarge: 'Images must be {{size}} KB or smaller',
      QuickActionImageType: 'Upload a PNG, JPEG or WebP image',
      QuickActionsReset: 'Reset to template',
      TagLabel: 'Tag',
      TagPlaceholder: 'Select a tag',
      TagsLabel: 'Tags',
      TagsInfo: 'Displays all configured tags',
      TagsEmpty: 'No tags configured.',
      TagUnavailable: 'The selected tag is no longer available. Select another tag.',
      CategoryFilter: 'Category filter',
      CampaignTypeFilter: 'Campaign type filter',
      SectionIssue: 'This section needs attention before it can be published.'
    },
    // TODO(AF-8): distance is a fixed placeholder; preview_items carries no distance.
    Canvas: {
      Label: 'Landing page canvas',
      Empty: 'No sections yet. Add sections to start building your landing page.',
      SelectTag: 'Select a tag to preview this section.',
      NoItems: 'No items to show in this section yet.',
      LoadFailed: 'Could not load preview items.',
      StaticSection: 'Shown with live customer data on the microsite.',
      LoyaltyCardTitle: 'Points balance',
      LoyaltyCardHint: "The customer's tier and points appear here.",
      QuickActionSample1: 'Quick action 1',
      QuickActionSample2: 'Quick action 2',
      QuickActionSample3: 'Quick action 3',
      QuickActionSample4: 'Quick action 4',
      QuickActionsHeadingCard1: 'Make the most of your points.',
      QuickActionsHeadingCard2: 'The Reward carnival is here.',
      RewardSampleTitle1: 'Private Vineyard Tour',
      RewardSampleTitle2: 'Weekend Spa Escape',
      RewardSampleTitle3: 'Dinner for Two',
      RewardSampleMerchant: 'Merchant name',
      RewardSampleDistance: '0.3 km',
      CategoryRewardsAllRewards: 'All Rewards',
      AllChallenges: 'All Challenges',
      RewardCta: 'View',
      ChallengeSampleTitle1: 'Weekly Step Challenge',
      ChallengeSampleTitle2: 'Spin & Win',
      ChallengeSampleTitle3: 'Daily Check-in Streak',
      ChallengeSampleDescription: 'Complete it to earn bonus points',
      ActiveStampSampleTitle: 'Stamp campaign title',
      ActiveRaffleSampleTitle: 'Raffle campaign title',
      ActiveStamps: 'Stamps',
      ActiveStampProgress: '{{current}}/{{total}} stamps',
      ActiveTickets: '{{count}} tickets',
      ActiveResume: 'Resume',
      ChallengeEndsInDay: 'Ends in {{count}} day',
      ChallengeEndsInDays: 'Ends in {{count}} days',
      StreakSampleTitle1: 'Sign in',
      StreakSampleTitle2: 'Transactions',
      StreakSampleTitle3: 'Daily check-in',
      StreakSampleRule1: 'Sign in for 7 consecutive days',
      StreakSampleRule2: '2 sign ins per month for 12 months',
      StreakSampleRule3: 'Check in 10 days in a row',
      StreakSampleUnit1: 'sign-ins',
      StreakSampleUnit2: 'transactions',
      StreakSampleUnit3: 'check-ins',
      StreakUnitDays: 'days',
      StreakLabel: 'Your streak',
      StreakWeekProgress: '{{current}} / {{target}} {{unit}} this week',
      StreakRemaining: 'Just {{count}} more {{unit}} this week to stay on track',
      StreakOnTrack: 'You are on track this week',
      PointRedemptionSampleTitle: 'Points to Cashback',
      PointRedemptionSampleDescription: 'Redeem your points to cashback',
      PointRedemptionValue: '{{points}} points = {{value}}',
      PointRedemptionValueShort: '{{points}} pts = {{value}}',
      PointRedemptionUnit: 'Points',
      PointRedemptionAccumulated: 'Accumulated Points',
      PointRedemptionCashback: 'Cashback Value',
      PointRedemptionCta: 'Redeem Now',
      TransferPointsSampleTitle: 'Transfer Points',
      TransferPointsSampleDescription: 'Share points to friends and family',
      TransferPointsCta: 'Transfer Now',
      Points: '{{points}} pts',
      SeeAll: 'See All',
      ShowLess: 'Show less',
      GoToItem: 'Go to item {{index}}',
      Distance: '1.2 km away',
      New: 'New',
      Upcoming: 'Upcoming',
      EndsOn: 'Ends {{date}}',
      LeaderboardRankLabel: 'Your leaderboard rank',
      LeaderboardSampleTitle1: 'Monthly Points Race',
      LeaderboardSampleTitle2: 'Weekend Spend Sprint',
      LeaderboardSampleTitle3: 'Referral Champions',
      LeaderboardSampleRank: 'Rank {{rank}}{{suffix}}',
      LeaderboardFeaturedRankSuffix: 'th',
      LeaderboardSampleRankSuffix: 'st'
    }
  },
  [PublishKey]: {
    Load: {
      Failed: 'Could not load the landing page.',
      Retry: 'Retry'
    },
    Actions: {
      Label: 'Landing page actions',
      SaveDraft: 'Save',
      Publish: 'Publish',
      ChangeStartingPoint: 'Change starting point',
      Unsaved: 'Unsaved changes',
      DraftSaved: 'Draft saved {{date}}',
      Published: 'Published {{date}}',
      NotPublished: 'Not published yet'
    },
    Toasts: {
      DraftSaved: 'Draft saved.',
      Published: 'Landing page published.',
      AlreadyPublished: 'Already published. There are no changes to publish.',
      SaveFailed: 'Could not save the draft. Please try again.',
      PublishFailed: 'Could not publish the landing page. Please try again.',
      Invalid: 'Fix the highlighted issues and try again.'
    },
    Issues: {
      Title: 'Fix these issues before publishing',
      GoToBranding: 'Go to Branding',
      Section: '{{name}}: {{issue}}',
      UnsupportedAttribute: 'Contains a setting that is not supported.',
      UnknownSection: 'This section is no longer available.',
      DuplicateSection: 'This section is added more than once.',
      InvalidLayout: 'The selected layout is not valid.',
      LayoutNotAllowed: 'This section does not support a layout.',
      UnknownQuickLink: 'A selected quick link is no longer available.',
      DuplicateQuickLink: 'A quick link is selected more than once.',
      ModuleDisabled: 'The module for this section is turned off.',
      InvalidTag: 'The selected tag is not valid.',
      TagRequired: 'Select a tag for this section.',
      TagUnavailable: 'The selected tag is no longer available. Select another tag.',
      ThemeIncomplete: 'The template theme is incomplete. Use Global Branding instead.',
      BrandingInvalid: 'Global Branding has invalid values. Fix them on the Branding tab.',
      BrandingIncomplete: 'Global Branding is missing required values: {{fields}}.',
      Generic: 'This configuration is not valid.'
    }
  },
  [PreviewKey]: {
    Open: 'Preview',
    Title: 'Landing page preview',
    Close: 'Close',
    Notice: 'Preview of your working changes. Nothing here is published.'
  },
  // TODO(AF-3): flag labels are placeholders until product confirms the exposed keys and copy.
  [ControlKey]: {
    Title: 'Control settings',
    Description: 'Turn microsite features on or off. Changes apply as soon as you toggle them.',
    Flags: {
      show_quest: 'Quests',
      show_nearme: 'Near me',
      rewards_carousel: 'Rewards carousel',
      show_point_transfer: 'Point transfer',
      show_popup_campaign: 'Pop-up campaigns',
      show_reward_nav_button: 'Reward navigation button',
      show_reward_favourite_button: 'Reward favourite button',
      show_progress_bar_campaigns_on_home_page: 'Progress bar campaigns on home page',
      show_instant_reward_campaigns_on_home_page: 'Instant reward campaigns on home page',
      manual_claim_raffle_ticket: 'Manual raffle ticket claim'
    },
    Toasts: {
      Saved: 'Control settings saved',
      SaveFailed: 'Could not save control settings. Please try again.'
    },
    Load: {
      Failed: 'Could not load control settings.',
      Retry: 'Retry'
    }
  },
  // TODO(AF-6): placeholder copy until product confirms the unsaved-changes dialog wording.
  [GuardKey]: {
    Title: 'Discard unsaved changes?',
    Content: 'You have unsaved changes. They will be lost if you leave this tab.',
    Discard: 'Discard',
    KeepEditing: 'Keep editing'
  }
};

// de/fr/it are typed against this so a key added here without its translations fails typecheck.
export type MicrositeTranslations = Catalogue<typeof translations>;
